import { motion } from "framer-motion";
import { Palette, BarChart3, ShoppingBag } from "lucide-react";

const services = [
  {
    icon: Palette,
    title: "Creative",
    items: ["Brand identity", "Product design", "Packaging design"],
  },
  {
    icon: BarChart3,
    title: "Strategy",
    items: ["Product positioning", "Range architecture", "Brand strategy"],
  },
  {
    icon: ShoppingBag,
    title: "Retail",
    items: ["Retail readiness", "Category positioning", "Consumer insight"],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-36 bg-stone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left — Statement */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-charcoal/50 block mb-6">
              What We Do
            </span>
            <h2
              data-testid="services-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] text-deep-black leading-[1.1] font-light"
            >
              Structured like a<br />
              <span className="font-medium">consultancy.</span>
              <br />
              Designed like a<br />
              <span className="font-medium">studio.</span>
            </h2>
            <p className="font-body text-base text-charcoal/60 mt-8 max-w-md leading-relaxed font-light">
              We combine creative design with commercial strategy to build
              brands and products that perform in real retail environments.
            </p>
          </motion.div>

          {/* Right — Cards */}
          <div className="flex flex-col gap-5">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  data-testid={`service-card-${service.title.toLowerCase()}`}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  variants={fadeUp}
                  custom={i + 1}
                  className="bg-off-white rounded-2xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow duration-500 group"
                >
                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-xl bg-stone flex items-center justify-center flex-shrink-0 group-hover:bg-deep-black transition-colors duration-500">
                      <Icon className="w-5 h-5 text-charcoal/60 group-hover:text-white transition-colors duration-500" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-deep-black tracking-tight font-medium mb-4">
                        {service.title}
                      </h3>
                      <ul className="space-y-2">
                        {service.items.map((item) => (
                          <li
                            key={item}
                            className="font-body text-sm text-charcoal/60 font-light"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
